/**
 * @author michalstolarski
 * @since 2023-4-6
 */

import * as React from "react";
import styled, { css, useTheme } from "styled-components";

import {
  END_OF_TENANCY_EXTRA_SERVICES,
  EXTRA_SERVICES_BOOKING,
} from "@typings/booking";
import { EXTRA_SERVICES_B2B } from "@typings/b2b";

import { rem, text, transition } from "@ui/helpers";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { OptionButtonProps, OptionButton } from "@ui/Atoms/OptionButton";
import { NormalizedList } from "@ui/Atoms/List";

import { mapTaskToIcon } from "./ExtraServiceV2.helpers";

interface ExtraServiceContainerProps {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
}

const ExtraServiceContainer = (
  props: ExtraServiceContainerProps,
): React.ReactElement => (
  <TestWrapper testId={props.testId}>
    <List className={props.className}>{props.children}</List>
  </TestWrapper>
);

interface ExtraServiceOptionProps
  extends Omit<OptionButtonProps, "children" | "type"> {
  icon:
    | EXTRA_SERVICES_BOOKING
    | END_OF_TENANCY_EXTRA_SERVICES
    | EXTRA_SERVICES_B2B;
  label: React.ReactNode;
  caption?: React.ReactNode;
}

const ExtraServiceOption = (
  props: ExtraServiceOptionProps,
): React.ReactElement => {
  const { testId, className = "", icon, label, caption, ...rest } = props;

  const theme = useTheme();
  const ServiceIcon = React.useMemo(
    () => mapTaskToIcon(icon) ?? React.Fragment,
    [icon],
  );

  return (
    <TestWrapper testId={testId}>
      <ListElement className={["no-styling-li", className].join(" ")}>
        <Option type="checkbox" {...rest}>
          <React.Fragment>
            <Icon>
              <ServiceIcon width={theme.icons.sizes.base_x3} />
            </Icon>

            <Label>{label}</Label>

            {!!caption && <Caption>{caption}</Caption>}
          </React.Fragment>
        </Option>
      </ListElement>
    </TestWrapper>
  );
};

export { ExtraServiceContainer, ExtraServiceOption };

const List = styled(NormalizedList)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(104)}, 1fr));
  gap: ${props => props.theme.margins.base_x2}
    ${props => props.theme.margins.base};

  &,
  * {
    user-select: none;
  }
`;

const ListElement = styled.li`
  display: grid;
`;

const Option = styled(OptionButton)`
  --option-button-font-color: currentColor;
  --option-button-font-color--hover: currentColor;
  --option-button-font-color--active: currentColor;
  --option-button-caption-color: ${props => props.theme.palette.grey400};

  ${props =>
    props.checked &&
    css`
      --option-button-caption-color: currentColor;
    `}

  font-size: ${props => props.theme.fonts.sizes.body2};
  font-weight: ${props => props.theme.fonts.weights.normal};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
  text-align: center;
  border-radius: ${props => props.theme.border.radius.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.margins.half};

  &:not(:hover) {
    --option-button-border-color: transparent;
  }

  * {
    pointer-events: none;
  }
`;

const Icon = styled.div`
  flex: 0 0 auto;
`;

const Label = styled.div`
  ${text("semibold_18")}

  flex: 1 1 100%;

  ${props => props.theme.breakpoints.tablet} {
    ${text("semibold_18")}
  }

  ${props => props.theme.breakpoints.desktop} {
    ${text("semibold_16")}
  }

  ${props => props.theme.breakpoints.desktopLarge} {
    ${text("semibold_18")}
  }
`;

const Caption = styled.div`
  ${text("regular_14")}

  color: var(--option-button-caption-color);
  transition: ${transition(["color"])};
`;
